<template>
  <!-- Installed system detail: components + maintenance visit history, with
       complete / skip actions that roll the schedule forward. -->
  <v-dialog v-model="show" width="900" scrollable>
    <v-card>
      <ModalHeader
        :title="system ? system.system_type + ' — ' + customerName : 'Installed System'"
        icon="mdi-cctv"
        :subtitle="system ? system.site_address : ''"
        @close="close"
      />
      <v-card-text class="pa-4" v-if="system">
        <div class="section-label">
          <v-icon x-small>mdi-information-outline</v-icon> Overview
        </div>
        <v-row dense class="mb-2">
          <v-col cols="6" sm="4">
            <div class="caption grey--text">Customer</div>
            <div>{{ customerName }}</div>
          </v-col>
          <v-col cols="6" sm="4">
            <div class="caption grey--text">Contact No.</div>
            <div v-if="contactNo" class="d-flex align-center">
              <span>{{ contactNo }}</span>
              <v-btn
                icon
                x-small
                class="ml-1"
                title="Copy number"
                @click="copyText(contactNo)"
              >
                <v-icon x-small>mdi-content-copy</v-icon>
              </v-btn>
            </div>
            <div v-else>—</div>
          </v-col>
          <v-col cols="6" sm="4">
            <div class="caption grey--text">End User Contact</div>
            <div v-if="endUserContact" class="d-flex align-center">
              <span>{{ endUserContact }}</span>
              <v-btn
                icon
                x-small
                class="ml-1"
                title="Copy number"
                @click="copyText(endUserContact)"
              >
                <v-icon x-small>mdi-content-copy</v-icon>
              </v-btn>
            </div>
            <div v-else>—</div>
          </v-col>
          <v-col cols="6" sm="4">
            <div class="caption grey--text">Account</div>
            <div>
              <v-chip x-small :color="isGovernment ? 'orange' : 'blue'" dark>
                <v-icon left x-small>{{ isGovernment ? "mdi-bank" : "mdi-walk" }}</v-icon>
                {{ isGovernment ? "Government" : "Walk-in" }}
              </v-chip>
            </div>
          </v-col>
          <v-col cols="6" sm="4">
            <div class="caption grey--text">Branch</div>
            <div>{{ (system.branch && system.branch.branch_name) || "—" }}</div>
          </v-col>
          <v-col cols="6" sm="4">
            <div class="caption grey--text">Municipality</div>
            <div>{{ system.site_municipality || "—" }}</div>
          </v-col>
          <v-col cols="6" sm="4">
            <div class="caption grey--text">Installed On</div>
            <div>{{ dateOnly(system.installed_on) }}</div>
          </v-col>
          <v-col cols="6" sm="4">
            <div class="caption grey--text">Free Until</div>
            <div>{{ dateOnly(system.free_until) || "—" }}</div>
          </v-col>
          <v-col cols="6" sm="4">
            <div class="caption grey--text">Schedule</div>
            <div>
              every {{ system.maintenance_cadence_months || 3 }} mo ·
              {{ maxVisits }} visits
            </div>
          </v-col>
          <v-col cols="6" sm="4">
            <div class="caption grey--text">Visit Mode</div>
            <div>
              <v-chip x-small :color="modeColor" dark>{{ modeLabel }}</v-chip>
            </div>
          </v-col>
          <v-col cols="6" sm="4">
            <div class="caption grey--text">Status</div>
            <div>
              <v-chip x-small :color="statusColor(system.status)" dark>{{
                system.status
              }}</v-chip>
            </div>
          </v-col>
        </v-row>
        <div v-if="system.notes" class="mb-2">
          <div class="caption grey--text">Notes</div>
          <div>{{ system.notes }}</div>
        </div>

        <div class="section-label mt-3">
          <v-icon x-small>mdi-chip</v-icon> Components
        </div>
        <v-simple-table dense class="bordered-table rounded-lg mb-3">
          <thead>
            <tr>
              <th>Description</th>
              <th style="width: 80px">Qty</th>
              <th>Serial No.</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in system.components" :key="c.id">
              <td>{{ c.description }}</td>
              <td>{{ c.quantity }}</td>
              <td>{{ c.serial_number || "—" }}</td>
            </tr>
            <tr v-if="!system.components || !system.components.length">
              <td colspan="3" class="text-center grey--text py-3">
                No components recorded
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <div class="section-label mt-3">
          <v-icon x-small>mdi-calendar-check</v-icon> Maintenance Visits
        </div>
        <v-simple-table dense class="bordered-table rounded-lg">
          <thead>
            <tr>
              <th style="width: 70px">Visit</th>
              <th style="width: 100px">Type</th>
              <th style="width: 120px">Due</th>
              <th style="width: 110px">Status</th>
              <th>Result</th>
              <th style="width: 150px">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="v in system.visits" :key="v.id">
              <td>{{ v.visit_number }} of {{ maxVisits }}</td>
              <td>
                <v-chip x-small outlined :color="v.visit_type === 'onsite' ? 'deep-purple' : 'blue'">
                  <v-icon left x-small>{{
                    v.visit_type === "onsite" ? "mdi-truck" : "mdi-phone"
                  }}</v-icon>
                  {{ v.visit_type === "onsite" ? "onsite" : "call" }}
                </v-chip>
              </td>
              <td>{{ dateOnly(v.due_date) }}</td>
              <td>
                <v-chip x-small :color="visitStatusColor(v.status)" dark>{{
                  v.status
                }}</v-chip>
              </td>
              <td class="text-caption">{{ v.result || "—" }}</td>
              <td>
                <template v-if="v.status === 'pending'">
                  <v-btn
                    x-small
                    color="success"
                    depressed
                    class="mr-1"
                    :loading="busyVisitId === v.id"
                    @click="openComplete(v)"
                    >Complete</v-btn
                  >
                  <v-btn
                    x-small
                    text
                    color="error"
                    :disabled="busyVisitId === v.id"
                    @click="openSkip(v)"
                    >Skip</v-btn
                  >
                </template>
                <span v-else class="grey--text caption">—</span>
              </td>
            </tr>
            <tr v-if="!system.visits || !system.visits.length">
              <td colspan="6" class="text-center grey--text py-3">
                No visits scheduled
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <p class="caption grey--text mt-2 mb-0">
          Completing a visit schedules the next one (until visit {{ maxVisits }}).
          Remote/call visits stay off the Field Order calendar; on-site visits
          appear there as Maintenance jobs.
        </p>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4 py-3">
        <span class="caption grey--text">installedSystemDetailModal.vue</span>
        <v-spacer />
        <v-btn text @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Complete visit dialog -->
    <v-dialog v-model="completeDialog" width="460" persistent>
      <v-card>
        <ModalHeader
          title="Complete Visit"
          icon="mdi-calendar-check"
          @close="completeDialog = false"
        />
        <v-card-text class="pa-4">
          <v-textarea
            v-model="completeResult"
            label="Result / what was found"
            rows="3"
            outlined
            dense
            hide-details
            auto-grow
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-spacer />
          <v-btn text @click="completeDialog = false">Cancel</v-btn>
          <v-btn color="success" depressed :loading="busyVisitId !== null" @click="doComplete"
            >Mark Completed</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Skip visit dialog -->
    <v-dialog v-model="skipDialog" width="460" persistent>
      <v-card>
        <ModalHeader
          title="Skip Visit"
          icon="mdi-calendar-remove"
          @close="skipDialog = false"
        />
        <v-card-text class="pa-4">
          <v-textarea
            v-model="skipReason"
            label="Reason"
            rows="3"
            outlined
            dense
            hide-details
            auto-grow
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-spacer />
          <v-btn text @click="skipDialog = false">Cancel</v-btn>
          <v-btn color="error" depressed :loading="busyVisitId !== null" @click="doSkip"
            >Skip Visit</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "InstalledSystemDetailModal",
  props: {
    value: { type: Boolean, default: false },
    systemId: { type: Number, default: null },
  },
  data() {
    return {
      system: null,
      busyVisitId: null,
      completeDialog: false,
      skipDialog: false,
      completeResult: "",
      skipReason: "",
      activeVisit: null,
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    customerName() {
      return (this.system && this.system.customer && this.system.customer.customer_name) || "—";
    },
    contactNo() {
      return (
        (this.system && this.system.customer && this.system.customer.customer_contact_no) || ""
      );
    },
    endUserContact() {
      return (this.system && this.system.end_user_contact) || "";
    },
    isGovernment() {
      return !!(
        this.system &&
        this.system.customer &&
        this.system.customer.is_government
      );
    },
    maxVisits() {
      return (this.system && this.system.max_visits) || 4;
    },
    visitMode() {
      if (!this.system) return "alternating";
      // Fall back to deriving from legacy has_remote_access.
      return (
        this.system.visit_mode ||
        (this.system.has_remote_access ? "alternating" : "onsite")
      );
    },
    modeLabel() {
      return { onsite: "Onsite", remote: "Call", alternating: "Alternating" }[
        this.visitMode
      ];
    },
    modeColor() {
      return { onsite: "deep-purple", remote: "blue", alternating: "teal" }[
        this.visitMode
      ];
    },
  },
  watch: {
    value(v) {
      if (v && this.systemId) this.load();
    },
  },
  methods: {
    ...mapActions({
      fetch_system: "installed_system/fetch_system",
      complete_visit: "installed_system/complete_visit",
      skip_visit: "installed_system/skip_visit",
    }),
    async load() {
      this.system = await this.fetch_system(this.systemId);
    },
    dateOnly(d) {
      return d ? String(d).substr(0, 10) : "";
    },
    statusColor(s) {
      return { active: "green", dormant: "orange", ended: "grey" }[s] || "grey";
    },
    visitStatusColor(s) {
      return { pending: "orange", completed: "green", skipped: "grey" }[s] || "grey";
    },
    openComplete(v) {
      this.activeVisit = v;
      this.completeResult = "";
      this.completeDialog = true;
    },
    openSkip(v) {
      this.activeVisit = v;
      this.skipReason = "";
      this.skipDialog = true;
    },
    async doComplete() {
      if (!this.activeVisit) return;
      this.busyVisitId = this.activeVisit.id;
      try {
        await this.complete_visit({
          id: this.activeVisit.id,
          result: this.completeResult,
        });
        this.completeDialog = false;
        await this.load();
        this.$emit("changed");
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            (e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
            "Failed to complete visit.",
          color: "error",
          icon: "mdi-alert",
        });
      } finally {
        this.busyVisitId = null;
      }
    },
    async doSkip() {
      if (!this.activeVisit) return;
      this.busyVisitId = this.activeVisit.id;
      try {
        await this.skip_visit({
          id: this.activeVisit.id,
          reason: this.skipReason,
        });
        this.skipDialog = false;
        await this.load();
        this.$emit("changed");
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            (e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
            "Failed to skip visit.",
          color: "error",
          icon: "mdi-alert",
        });
      } finally {
        this.busyVisitId = null;
      }
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(String(text));
        this.$store.dispatch("notification/set_notification", {
          message: "Contact number copied",
          type: "success",
        });
      } catch (e) {
        this.$store.dispatch("notification/set_notification", {
          message: "Copy failed — " + String(text),
          type: "error",
        });
      }
    },
    close() {
      this.show = false;
    },
  },
};
</script>
